import { FunctionComponent, useEffect, useState } from 'react';
import { Typography, Paper, LinearProgress, Input, Dialog } from '../../tailwindcomponents';
import SecurityApi from '../../api/security';
import { SmallPackageInformation } from '../../components';
import { AircraftInHangar } from '../../types/security';
import StaffApi from '../../api/staffmanagement';
import { StaffManagementBays } from '../../types/staffManagement';
import { HangarChecklistDto } from '../../types/checklist';
import ChecklistDetailsModal from '../Checklists/ChecklistDetailsModal';
import ChecklistsApi from '../../api/checklists';
import { useSelector } from "react-redux";
import { RootState } from "../../reducer";
import ChecklistApi from "../../api/checklists";
import { Datepicker } from '@mobiscroll/react';
import OverviewApi from '../../api/overview';
import '@mobiscroll/react/dist/css/mobiscroll.min.css';
import { MbscCalendarEvent } from '@mobiscroll/react';
import { PackageInformationObj } from '../../types/overview';

interface selectedVariables {
  description: string;
  status: boolean;
  inDate: Date;
  outDate: Date;
}
type HangarProps = {
  isLinePlannerTab: boolean;
}

const HangarOverview: FunctionComponent<HangarProps> = ({ isLinePlannerTab = false }) => {
  const [hangarOverviewleftCol, setHangarOverviewleftCol] = useState(Array<AircraftInHangar>());
  const [hangarOverviewRightCol, setHangarOverviewRightCol] = useState(Array<AircraftInHangar>());
  const [staffManagementBays, setStaffManagementBays] = useState<StaffManagementBays[]>([]);
  const [checklist, setChecklist] = useState<HangarChecklistDto>();
  const [checklistDialogOpen, setChecklistDialogOpen] = useState<boolean>(false);
  const [createChecklistDialog, setCreateChecklistDialog] = useState<boolean>(false);
  const [createChecklistFields, setCreateChecklistField] = useState<boolean>(false);
  const [selectedTailNumber, setSelectedTailNumber] = useState<string>("");
  const [selected, setSelected] = useState<selectedVariables>({ description: "", status: false, inDate: new Date(), outDate: new Date() });
  const [loading, setLoading] = useState<boolean>(false);
  const me = useSelector((state: RootState) => state.user.me);
  const isChecklistAdmin = me.user_clearence_groups.includes("Checklist Edit");

  useEffect(() => {
    setLoading(true);
    refresh();
  }, []);

  const refresh = () => {
    SecurityApi.AircraftInHangar().then(res => {
      sortHangarOverview(res.data);
      StaffApi.GetAllStaffManagement().then(res => {
        setStaffManagementBays(res.data);
        setLoading(false);
      });
    });
  }

  const sortHangarOverview = (hangarOverview: AircraftInHangar[]): void => {
    const leftCol = hangarOverview.filter((x) => x.bay_name === 'Bay 4' || x.bay_name === 'Bay 5' || x.bay_name === 'Bay 6' || x.bay_name === 'Bay 7');
    const rightCol = hangarOverview.filter((x) => x.bay_name !== 'Bay 4' && x.bay_name !== 'Bay 5' && x.bay_name !== 'Bay 6' && x.bay_name !== 'Bay 7');

    const sortleft = leftCol.sort((a, b) => a.bay_name.localeCompare(b.bay_name));
    const sortRight = rightCol.sort((a, b) => a.bay_name.localeCompare(b.bay_name));

    setHangarOverviewleftCol(sortleft);
    setHangarOverviewRightCol(sortRight);
  }

  const getSupervisorNames = (currentBay: string): string[] =>
    staffManagementBays.reduce<string[]>(
      (acc, bay) =>
        bay.bay.toLowerCase() === currentBay.toLowerCase()
          ? acc.concat(
            (bay.areas.find(area => area.area.toUpperCase().includes("SUPERVISORS"))?.employyes_in_group || []).map(emp => emp.employee_name)
          )
          : acc,
      []
    );

  const getEarliestAndLatestDates = (rows: PackageInformationObj[]) => {
    const startDates = rows.map(row => new Date(row.wo_actual_start_date || row.wo_schedule_start_date)).filter(date => !isNaN(date.getTime()));
    const stopDates = rows.map(row => new Date(row.wo_actual_stop_date || row.wo_schedule_stop_date)).filter(date => !isNaN(date.getTime()));

    const earliestStart = startDates.length
      ? new Date(Math.min(...startDates.map(d => d.getTime())))
      : new Date();

    const latestStop = stopDates.length
      ? new Date(Math.max(...stopDates.map(d => d.getTime())))
      : new Date();
    return { earliestStart, latestStop };
  };

  const getChecklistInfo = (tailNumber: string) => {
    if (tailNumber !== "") {
      OverviewApi.GetPackageInformationByTailNumber(tailNumber, ["IN WORK"], false).then(res => {
        if (res.status === true) {
          const { earliestStart, latestStop } = getEarliestAndLatestDates(res.data);
          setSelected(prev => ({
            ...prev,
            inDate: new Date(earliestStart),
            outDate: new Date(latestStop),
          }));
        }
      })
    }
    setLoading(true);
    ChecklistsApi.GetChecklist(tailNumber).then(res => {
      if (res.status === true) {
        setChecklist(res.checklist);
        setChecklistDialogOpen(true);
      }
      else {
        if (isChecklistAdmin) {
          setCreateChecklistDialog(true);
          setSelectedTailNumber(tailNumber);
        }
      }
      setLoading(false);
    });
  }

  const onCreateChecklist = (status: boolean) => {
    if (status && !createChecklistFields) {
      setCreateChecklistField(true);
    }
    else if (status && createChecklistFields) {
      createChecklist();
    }
    else {
      setCreateChecklistField(false);
      setChecklistDialogOpen(false);
      setCreateChecklistDialog(false);
    }
  }

  const createChecklist = () => {
    setLoading(true);
    ChecklistApi.CreateChecklist(selectedTailNumber, selected.description, selected.status, new Date(selected.inDate), new Date(selected.outDate)).then(res => {
      refresh();
      setChecklistDialogOpen(false);
      setCreateChecklistDialog(false);
      getChecklistInfo(selectedTailNumber);
    });
  }

  return (
    <div className="max-w-full mx-auto px-4 w-full">
      {(loading && isLinePlannerTab === false) && <LinearProgress />}
      {/* Header */}
      <Typography variant="h1" align="center" text={"Hangar Overview"} />

      {/* Main Grid */}
      <div
        className="flex gap-2 flex-nowrap mt-px"
      >
        {/* Left Column */}
        <div
          className="flex-[1_1_48%] min-w-[300px] max-w-[calc(50%-8px)] pr-px"
        >
          {hangarOverviewleftCol.map((row, idx) => (
            <Paper key={idx} className="mb-2 p-2">
              <SmallPackageInformation
                packageInformation={row}
                supervisors={getSupervisorNames(row.bay_name)}
                onTailNumberClicked={() => getChecklistInfo(row.tail_number)}
              />
            </Paper>
          ))}
        </div>

        {/* Right Column */}
        <div
          className="flex-[1_1_48%] min-w-[300px] max-w-[calc(50%-8px)]"
        >
          {hangarOverviewRightCol.map((row, idx) => (
            <Paper key={idx} className="mb-2 p-2">
              <SmallPackageInformation
                packageInformation={row}
                supervisors={getSupervisorNames(row.bay_name)}
                onTailNumberClicked={() => getChecklistInfo(row.tail_number)}
              />
            </Paper>
          ))}
        </div>
      </div>
      <Dialog
        visible={checklistDialogOpen}
        max_width={"md"}
        title={checklist?.TailNumber || ""}
        context={
          <ChecklistDetailsModal
            checklist={checklist}
            checkListDialogOpen={setChecklistDialogOpen}
            isChecklistAdmin={isChecklistAdmin}
            refreshChecklists={() => { }}
            setLoading={setLoading}
            loading={loading}
          />
        }
        onClose={(status: Boolean) => { setChecklistDialogOpen(false); setLoading(false) }}
        strict={false}
        show_options={false}
      />
      <Dialog
        visible={createChecklistDialog}
        max_width={"lg"}
        title={`Would you like to create a checklist for ${selectedTailNumber}` || ""}
        context={
          <div>
            {createChecklistFields &&
              <div className="flex flex-row flex-wrap justify-center mt-7">
                <div className="w-1/4 md:w-1/4 lg:w-1/4 xl:w-1/4 mr-4">
                  <Input
                    multiline={false}
                    help_text={""}
                    label_text={"Description"}
                    value={selected.description}
                    onTextChange={(text: string) =>
                      setSelected(prevState => ({
                        ...prevState,
                        description: text,
                      }))
                    }
                    type="text"
                    width={100}
                  />
                </div>
                <div className="w-1/6 md:w-1/6 lg:w-1/6 xl:w-1/6 mr-4">
                  <div className="flex items-center">
                    <label
                      htmlFor="datepicker-id"
                      className="whitespace-nowrap text-[0.9rem] leading-[1.5]"
                    >
                      In
                    </label>
                    <Datepicker
                      inputProps={{
                        id: 'datepicker-id',
                        placeholder: 'Select date',
                      }}
                      controls={['calendar']}
                      display="anchored"
                      touchUi={true}
                      value={selected.inDate}
                      onChange={(e: MbscCalendarEvent) => {
                        setSelected((prevState) => ({
                          ...prevState,
                          inDate: e.value,
                        }));
                      }}
                      themeVariant="light"
                      dateFormat="DD.MM.YYYY"
                    />
                  </div>
                </div>
                <div className="w-1/6 md:w-1/6 lg:w-1/6 xl:w-1/6 mr-4">
                  <div className="flex items-center">
                    <label
                      htmlFor="datepicker-id"
                      className="whitespace-nowrap text-[0.9rem] leading-[1.5]"
                    >
                      Out
                    </label>
                  <Datepicker
                    controls={['calendar']}
                    inputProps={{
                      placeholder: 'Select date'
                    }}
                    display='anchored'
                    touchUi={true}
                    value={selected.outDate}
                    onChange={(e: MbscCalendarEvent) => {
                      setSelected(prevState => ({
                        ...prevState,
                        outDate: e.value,
                      }))
                    }
                    }
                    themeVariant='light'
                    dateFormat='DD.MM.YYYY'
                  />
                </div>
                </div>
              </div>
            }
          </div>
        }
        onClose={(status: boolean) => onCreateChecklist(status)}
        strict={false}
        show_options={true}
      />
    </div>
  );
};
export default HangarOverview;